import { useClerk } from "@clerk/clerk-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Hourglass } from "lucide-react";
import { useEffect } from "react";

import { SubscriptionError } from "@/components/layout/subscription-error";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useSubscriptionStatus } from "@/lib/use-is-subscribed";

// /waiting-for-activation — the holding page for signed-in users whose
// subscription is not yet active.
//
// Lives outside `/signin` and `/signup` (which are themselves outside
// `/_authenticated`) so the auth+subscription gate in
// `auth-required-layout.tsx` doesn't loop. Sign-out lands on /signin;
// re-login will bounce back here while subscription is still off.
//
// The layout redirects here only when the status is genuinely
// 'inactive' — an API error during the layout's gate shows
// `<SubscriptionError />` instead. The defensive 'error' branch below
// covers the case where the endpoint starts erroring mid-poll (e.g.
// the backend goes down after the gate admitted the user).
//
// UX:
//   • Headline: "Your subscription is being activated"
//   • One-line hint: "We'll let you in as soon as an admin flips the
//     switch on your account."
//   • Spinner + "Checking every 30 seconds" — drives the user to
//     expect auto-update; not an interval timer they'd notice.
//   • "Sign out" button (always useful — they may want to use a
//     different account).
//
// ponytail: the page mounts its own useQuery indirectly via
// useSubscriptionStatus() with the same query key as the layout
// (`['me', 'subscription']`) AND queryClient shared at root, so the
// data is the same one the layout would re-read on the next render;
// once the admin flips the flag, refetchInterval=30s re-fetches and
// useSubscriptionStatus() flips to 'active' and we navigate to '/'.
// refetchOnWindowFocus is also enabled in main.tsx for prod, so a
// user clicking back to the tab advances this without waiting 30s.
const POLL_INTERVAL_MS = 30 * 1000;

export const Route = createFileRoute("/waiting-for-activation")({
  component: WaitingForActivationRoute,
});

export function WaitingForActivationRoute() {
  const navigate = useNavigate();
  const clerk = useClerk();
  const subscription = useSubscriptionStatus({ refetchInterval: POLL_INTERVAL_MS });

  // ponytail: once the subscription flips to active, drop the user
  // back to the app root. The auth-required layout sees the same
  // query flip (shared client + key) and lets them through.
  useEffect(() => {
    if (subscription.status === "active") navigate({ to: "/" });
  }, [subscription, navigate]);

  if (subscription.status === "error") {
    return <SubscriptionError error={subscription.error} refetch={subscription.refetch} />;
  }

  return (
    <div className="flex min-h-svh items-center justify-center px-4 py-8">
      <Card className="w-full max-w-md">
        <CardHeader className="items-center text-center">
          <Hourglass className="text-muted-foreground mb-2 h-10 w-10" />
          <CardTitle>Your subscription is being activated</CardTitle>
          <CardDescription>
            An admin needs to enable your account before you can use the app. This page checks for
            activation automatically — you don&apos;t need to refresh.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center gap-3">
          <div
            className="text-muted-foreground flex items-center gap-2 text-sm"
            role="status"
            aria-live="polite"
            aria-busy={subscription.status === "loading" ? "true" : "false"}
          >
            <span className="bg-muted-foreground/40 inline-block h-2 w-2 animate-pulse rounded-full" />
            Checking every 30 seconds
          </div>
          <Button
            variant="outline"
            onClick={() => {
              // ponytail: sign-out errors are non-fatal — see
              // /signout for the same fire-and-forget pattern.
              void clerk.signOut().catch(() => undefined);
              navigate({ to: "/signin" });
            }}
          >
            Sign out
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
